<template>
  <div class="h-100 w-100">
    <div class="d-flex justify-content-between align-items-center">
      <h5
        v-if="title"
        class="m-0 text-truncate"
        :title="title"
        v-html="title"
      />

      <div class="d-flex justify-content-end w-100">
        <v-chart-dropdown
          name="chart"
          :extra-actions="extraActions"
          @extra-action-clicked="(action) => $emit(action)"
        />
      </div>
    </div>

    <canvas
      ref="chart"
      class="chart"
    />

    <!-- Invisible chart for downloading -->
    <canvas
      ref="chartDownload"
      class="chart-download"
    ></canvas>
  </div>
</template>

<script>
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  registerables,
} from 'chart.js';
import {
  defaultChartOptions,
  defaultDataLabels,
} from '@/organisation/services/charts';
import ChartDropdown from '@/organisation/components/dashboard/ChartDropdown.vue';
import { COLORS_WITHOUT_WHITE } from '@/common/utils/constants';
import { truncate } from '@/common/utils/string';

export default {
  name: 'BarChart',
  components: {
    'v-chart-dropdown': ChartDropdown,
  },
  props: {
    dataSets: {
      // An array of objects like:
      // [
      //   {
      //     label: 'bar',
      //     data: [1, 2, 3, 4],
      //     backgroundColor: ['#fff', '#E5S12F'],
      //   },
      // ]
      required: true,
      type: Array,
    },
    labels: {
      required: true,
      type: Array,
    },
    showLegend: {
      type: Boolean,
      default() {
        return true;
      },
    },
    truncateYLabels: {
      type: Number,
      required: false,
    },
    direction: {
      type: String,
      default() {
        return 'vertical';
      },
    },
    title: {
      required: false,
    },
    downloadEnabled: {
      type: Boolean,
      default() {
        return true;
      },
    },
    extraActions: {
      // An array of objects like:
      // [
      //   {
      //     name: 'thisAction',
      //     title: 'word.title',
      //     icon: 'info',
      //   }
      // ]
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      chart: null,
      copied: false,
    };
  },
  methods: {
    async createChart() {
      Chart.register(...registerables);
      const dataSets = this.dataSets.map((dataSet, index) => ({
        label: dataSet.label,
        data: dataSet.data,
        backgroundColor: dataSet.backgroundColor || COLORS_WITHOUT_WHITE[index],
        datalabels: defaultDataLabels({ size: 'sm' }),
      }));
      const component = this;

      // eslint-disable-next-line no-new
      this.chart = new Chart(
        this.$refs.chart.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels: this.labels,
            datasets: dataSets,
          },
          plugins: [
            ChartDataLabels,
          ],
          options: {
            ...defaultChartOptions(
              null,
              this.showLegend,
              true,
              {},
              true,
            ),
            layout: {
              padding: {
                top: 0,
              },
            },
            scales: {
              y: {
                grid: {
                  display: false,
                  drawBorder: true,
                },
                ticks: {
                  callback(value) {
                    const displayValue = this.getLabelForValue(value);
                    if (displayValue) {
                      if (component.truncateYLabels) {
                        return truncate(displayValue, component.truncateYLabels);
                      }
                      return displayValue;
                    }
                    return '';
                  },
                },
              },
              x: {
                grid: {
                  display: false,
                  drawBorder: false,
                },
              },
            },
            indexAxis: this.direction === 'vertical' ? 'x' : 'y',
          },
        },
      );

      await this.createDownloadableChart(
        'bar',
        dataSets,
      );
    },
    async createDownloadableChart(chartType, dataSets) {
      // eslint-disable-next-line no-new
      new Chart(
        this.$refs.chartDownload.getContext('2d'),
        {
          type: chartType,
          plugins: [
            ChartDataLabels,
          ],
          data: {
            labels: this.labels,
            datasets: dataSets,
          },
          options: {
            ...defaultChartOptions(
              null,
              this.showLegend,
              true,
              {},
              true,
            ),
            indexAxis: 'y',
          },
        },
      );
    },
  },
  mounted() {
    this.createChart();
  },
};
</script>

<style scoped>
canvas.chart-lg {
  max-height: 302px;
  min-height: 300px;
}

canvas.chart-md {
  max-height: 152px;
  min-height: 150px;
}

canvas.chart-sm {
  height: 100px;
  max-height: 100px;
}
</style>
